import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Badge,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Add, Notifications, CloudUpload } from "@mui/icons-material";
import { partnerDashboardService } from "../../../apis/partnerDashboard";
import { uploadImage } from "../../../apis/drivers";
import axios from "axios";

interface Car {
  _id: string;
  car_make: string;
  car_model: string;
  license_plate: string;
  driver_id: string;
  driver_name?: string;
  owner_id?: string;
  owner_name?: string;
  status: string;
  is_offline?: boolean;
  offline_since?: string;
  car_year?: number;
  total_seats?: number;
  fuel_type?: string;
  ac_working?: boolean;
  vehicle_type?: string;
  car_type?: string;
  delivery_capacity?: {
    max_weight?: number;
    max_width?: number;
    max_height?: number;
    max_length?: number;
  };
  list_document_id?: Array<{name: string; url: string}> | string[];
  list_document_url?: Array<{name: string; url: string}> | string[];
}

interface PartnerCarsProps {
  partnerId: string;
}

const fuelTypes = ["ELECTRIC", "HYBRID", "GASOLINE", "DIESEL", "PETROL"];
const vehicleTypes = ["CAR", "MOTOR_BIKE", "VAN", "TRIKE", "MEDIUM_TRUCK", "LARGE_TRUCK"];
const carMakes = [
  "Ford",
  "Chevrolet",
  "Toyota",
  "SUZUKI",
  "Honda",
  "Nissan",
  "BMW",
  "Mercedes-Benz",
  "Volkswagen",
  "Audi",
  "Hyundai",
  "Kia",
  "Subaru",
  "Mazda",
  "Lexus",
  "Jaguar",
  "Land Rover",
  "Porsche",
  "Ferrari",
  "Lamborghini",
  "Tesla",
  "Others",
];

const PartnerCars: React.FC<PartnerCarsProps> = ({ partnerId }) => {
  const [cars, setCars] = useState<Car[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAssignDriverModal, setOpenAssignDriverModal] = useState(false);
  const [openAssignOwnerModal, setOpenAssignOwnerModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [editFormData, setEditFormData] = useState({
    car_make: "",
    car_model: "",
    license_plate: "",
    car_year: "",
    total_seats: "",
    fuel_type: "",
    driver_id: "",
    owner_id: "",
    ac_working: false,
    vehicle_type: "",
    delivery_capacity: {
      max_weight: "",
      max_width: "",
      max_height: "",
      max_length: "",
    },
  });
  const [selectedDriverId, setSelectedDriverId] = useState<string>("");
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("");
  const [formData, setFormData] = useState({
    car_make: "",
    car_model: "",
    license_plate: "",
    car_year: "",
    total_seats: "",
    fuel_type: "",
    driver_id: "",
    owner_id: "",
    ac_working: false,
    vehicle_type: "",
    delivery_capacity: {
      max_weight: "",
      max_width: "",
      max_height: "",
      max_length: "",
    },
  });
  const [carRegistrationPhoto, setCarRegistrationPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [editCarRegistrationPhoto, setEditCarRegistrationPhoto] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [offlineNotifications, setOfflineNotifications] = useState<Car[]>([]);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: "", 
    severity: "success" as "success" | "error" | "warning" 
  });

  useEffect(() => {
    fetchCars();
    fetchDrivers();
    fetchOwners();
  }, [partnerId]);

  useEffect(() => {
    // Check for offline cars
    const offline = cars.filter((car) => car.is_offline);
    setOfflineNotifications(offline);
  }, [cars]);

  const fetchCars = async () => {
    setLoading(true);
    try {
      const carsData = await partnerDashboardService.getPartnerCars(partnerId);
      console.log("Fetched cars data:", carsData);
      setCars(Array.isArray(carsData) ? carsData : []);
    } catch (error: any) {
      console.error("Error fetching cars:", error);
      console.error("Error response:", error.response);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch cars",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const driversData = await partnerDashboardService.getPartnerDrivers(partnerId);
      setDrivers(Array.isArray(driversData) ? driversData : []);
    } catch (error) {
      console.error("Failed to fetch drivers:", error);
    }
  };

  const fetchOwners = async () => {
    try {
      const ownersData = await partnerDashboardService.getOwners(partnerId);
      setOwners(Array.isArray(ownersData) ? ownersData : []);
    } catch (error) {
      console.error("Failed to fetch owners:", error);
    }
  };

  const handleAssignDriver = (car: Car) => {
    setSelectedCar(car);
    setSelectedDriverId(car.driver_id || "");
    setOpenAssignDriverModal(true);
  };

  const handleAssignOwner = (car: Car) => {
    setSelectedCar(car);
    setSelectedOwnerId(car.owner_id || "");
    setOpenAssignOwnerModal(true);
  };

  const handleSubmitAssignDriver = async () => {
    if (!selectedCar || !selectedDriverId) {
      setSnackbar({
        open: true,
        message: "Please select a driver",
        severity: "error",
      });
      return;
    }

    try {
      await partnerDashboardService.updateCarDriver(selectedCar._id, selectedDriverId);
      setSnackbar({
        open: true,
        message: "Driver assigned successfully",
        severity: "success",
      });
      setOpenAssignDriverModal(false);
      setSelectedCar(null);
      fetchCars();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Failed to assign driver. Driver may have an active ride.",
        severity: "error",
      });
    }
  };

  const handleSubmitAssignOwner = async () => {
    if (!selectedCar) {
      setSnackbar({
        open: true,
        message: "No car selected",
        severity: "error",
      });
      return;
    }

    try {
      await partnerDashboardService.updateCarOwner(
        selectedCar._id,
        selectedOwnerId || null
      );
      setSnackbar({
        open: true,
        message: "Owner assigned successfully",
        severity: "success",
      });
      setOpenAssignOwnerModal(false);
      setSelectedCar(null);
      fetchCars();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to assign owner",
        severity: "error",
      });
    }
  };

  const handleEditCar = (car: Car) => {
    setSelectedCar(car);
    // Extract delivery_capacity from car
    const deliveryCapacity = car.delivery_capacity || {};
    setEditFormData({
      car_make: car.car_make || "",
      car_model: car.car_model || "",
      license_plate: car.license_plate || "",
      car_year: car.car_year?.toString() || "",
      total_seats: car.total_seats?.toString() || "",
      fuel_type: car.fuel_type || "",
      driver_id: car.driver_id || "",
      owner_id: car.owner_id || "",
      ac_working: car.ac_working ?? false,
      vehicle_type: car.vehicle_type || car.car_type || "",
      delivery_capacity: {
        max_weight: deliveryCapacity.max_weight?.toString() || "",
        max_width: deliveryCapacity.max_width?.toString() || "",
        max_height: deliveryCapacity.max_height?.toString() || "",
        max_length: deliveryCapacity.max_length?.toString() || "",
      },
    });
    
    // Extract existing car registration photo URL
    const documents: Array<{name: string; url: string}> | string[] = car.list_document_url || car.list_document_id || [];
    let existingPhotoUrl: string | null = null;
    if (Array.isArray(documents) && documents.length > 0) {
      // Type guard to check if it's an array of objects
      if (documents.length > 0 && typeof documents[0] === 'object' && documents[0] !== null && 'name' in documents[0]) {
        const photoDoc = (documents as Array<{name: string; url: string}>).find((doc) => 
          doc.name === "CarRegistrationPhoto"
        );
        if (photoDoc) {
          existingPhotoUrl = photoDoc.url;
        } else if (documents[0] && 'url' in documents[0]) {
          existingPhotoUrl = (documents[0] as {name: string; url: string}).url;
        }
      } else {
        // It's an array of strings
        const photoDoc = (documents as string[]).find((doc) => 
          typeof doc === 'string' && doc.includes("CarRegistrationPhoto")
        );
        if (photoDoc) {
          existingPhotoUrl = photoDoc;
        } else if (documents[0] && typeof documents[0] === 'string') {
          existingPhotoUrl = documents[0];
        }
      }
    }
    setEditPhotoPreview(existingPhotoUrl);
    setEditCarRegistrationPhoto(null);
    
    setOpenEditModal(true);
  };

  const handleUpdateCar = async () => {
    if (!selectedCar || !editFormData.car_make || !editFormData.car_model || !editFormData.license_plate) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields (Make, Model, License Plate)",
        severity: "error",
      });
      return;
    }

    setUploading(true);
    try {
      let list_document_id: Array<{name: string; url: string}> = [];

      // Upload new car registration photo if provided
      if (editCarRegistrationPhoto) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", editCarRegistrationPhoto);

        const uploadResponse = await uploadImage(formDataUpload);
        console.log("S3 Upload Response:", uploadResponse);
        
        // S3 service returns: { message: "Uploaded the files successfully: [filename]" }
        // Extract filename from the message and construct download URL
        if (uploadResponse?.data?.message) {
          const message = uploadResponse.data.message;
          // Extract filename from message like "Uploaded the files successfully: [filename]"
          const match = message.match(/\[(.*?)\]/);
          if (match && match[1]) {
            const filename = match[1];
            // Construct download URL
            const S3_SERVICE_URL = process.env.REACT_APP_S3_SERVICE_URL || "";
            const baseUrl = S3_SERVICE_URL.replace(/\/$/, ""); // Remove trailing slash
            const downloadUrl = `${baseUrl}/s3/download/${filename}`;
            
            list_document_id.push({
              name: "CarRegistrationPhoto",
              url: downloadUrl
            });
          } else {
            console.error("Could not extract filename from upload response:", uploadResponse);
            throw new Error("Failed to extract filename from upload response");
          }
        } else {
          console.error("Upload response:", uploadResponse);
          throw new Error("Failed to upload car registration photo");
        }
      }

      const carData: any = {
        car_make: editFormData.car_make,
        car_model: editFormData.car_model,
        license_plate: editFormData.license_plate,
        car_year: editFormData.car_year ? parseInt(editFormData.car_year) : undefined,
        total_seats: editFormData.total_seats ? parseInt(editFormData.total_seats) : undefined,
        fuel_type: editFormData.fuel_type || undefined,
        ac_working: editFormData.ac_working,
        vehicle_type: editFormData.vehicle_type || undefined,
      };

      // Only include driver_id if a driver is selected
      if (editFormData.driver_id) {
        carData.driver_id = editFormData.driver_id;
      }
      
      // Include owner_id (can be empty string to unassign)
      carData.owner_id = editFormData.owner_id || null;

      // Add list_document_id if new file was uploaded
      if (list_document_id.length > 0) {
        carData.list_document_id = list_document_id;
      }

      // Add delivery_capacity if all fields are filled
      if (
        editFormData.delivery_capacity.max_weight &&
        editFormData.delivery_capacity.max_width &&
        editFormData.delivery_capacity.max_height &&
        editFormData.delivery_capacity.max_length
      ) {
        carData.delivery_capacity = {
          max_weight: parseFloat(editFormData.delivery_capacity.max_weight),
          max_width: parseFloat(editFormData.delivery_capacity.max_width),
          max_height: parseFloat(editFormData.delivery_capacity.max_height),
          max_length: parseFloat(editFormData.delivery_capacity.max_length),
        };
      }

      await partnerDashboardService.updateCar(selectedCar._id, carData);
      setSnackbar({
        open: true,
        message: editFormData.driver_id
          ? "Car updated successfully with driver assigned"
          : "Car updated successfully",
        severity: "success",
      });
      setOpenEditModal(false);
      setSelectedCar(null);
      setEditCarRegistrationPhoto(null);
      setEditPhotoPreview(null);
      fetchCars();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Failed to update car. Driver may have an active ride.",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCarRegistrationPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setEditCarRegistrationPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateCar = async () => {
    if (!formData.car_make || !formData.car_model || !formData.license_plate) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields (Make, Model, License Plate)",
        severity: "error",
      });
      return;
    }

    // Safety check: Ensure no pending driver is selected (should be prevented by disabled state)
    const selectedDriver = drivers.find((d) => d._id === formData.driver_id);
    if (selectedDriver?.isPending) {
      setSnackbar({
        open: true,
        message: "Cannot assign car to invited driver yet. Please create the car without a driver and assign it after the driver signs up.",
        severity: "warning",
      });
      return;
    }

    // Driver is optional - can be assigned later

    setUploading(true);
    try {
      let list_document_id: Array<{name: string; url: string}> = [];

      // Upload car registration photo if provided
      if (carRegistrationPhoto) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", carRegistrationPhoto);

        const uploadResponse = await uploadImage(formDataUpload);
        console.log("S3 Upload Response:", uploadResponse);
        
        // S3 service returns: { message: "Uploaded the files successfully: [filename]" }
        // Extract filename from the message and construct download URL
        if (uploadResponse?.data?.message) {
          const message = uploadResponse.data.message;
          // Extract filename from message like "Uploaded the files successfully: [filename]"
          const match = message.match(/\[(.*?)\]/);
          if (match && match[1]) {
            const filename = match[1];
            // Construct download URL
            const S3_SERVICE_URL = process.env.REACT_APP_S3_SERVICE_URL || "";
            const baseUrl = S3_SERVICE_URL.replace(/\/$/, ""); // Remove trailing slash
            const downloadUrl = `${baseUrl}/s3/download/${filename}`;
            
            list_document_id.push({
              name: "CarRegistrationPhoto",
              url: downloadUrl
            });
          } else {
            console.error("Could not extract filename from upload response:", uploadResponse);
            throw new Error("Failed to extract filename from upload response");
          }
        } else {
          console.error("Upload response:", uploadResponse);
          throw new Error("Failed to upload car registration photo");
        }
      }

      const carData: any = {
        car_make: formData.car_make,
        car_model: formData.car_model,
        license_plate: formData.license_plate,
        car_year: formData.car_year ? parseInt(formData.car_year) : undefined,
        total_seats: formData.total_seats ? parseInt(formData.total_seats) : undefined,
        fuel_type: formData.fuel_type || undefined,
        ac_working: formData.ac_working,
        vehicle_type: formData.vehicle_type || undefined,
      };
      
      // Only include driver_id if a driver is selected
      if (formData.driver_id && selectedDriver && !selectedDriver.isPending) {
        carData.driver_id = formData.driver_id;
      }
      
      // Include owner_id if an owner is selected (optional)
      if (formData.owner_id) {
        carData.owner_id = formData.owner_id;
      }

      // Add list_document_id if file was uploaded (format: array of objects with name and url)
      if (list_document_id.length > 0) {
        carData.list_document_id = list_document_id;
      }

      // Add delivery_capacity if all fields are filled
      if (
        formData.delivery_capacity.max_weight &&
        formData.delivery_capacity.max_width &&
        formData.delivery_capacity.max_height &&
        formData.delivery_capacity.max_length
      ) {
        carData.delivery_capacity = {
          max_weight: parseFloat(formData.delivery_capacity.max_weight),
          max_width: parseFloat(formData.delivery_capacity.max_width),
          max_height: parseFloat(formData.delivery_capacity.max_height),
          max_length: parseFloat(formData.delivery_capacity.max_length),
        };
      }
      
      await partnerDashboardService.createCar(carData);
      setSnackbar({
        open: true,
        message: formData.driver_id && selectedDriver && !selectedDriver.isPending
          ? "Car created successfully with driver assigned"
          : "Car created successfully. You can assign a driver later.",
        severity: "success",
      });
      setOpenAddModal(false);
      setFormData({
        car_make: "",
        car_model: "",
        license_plate: "",
        car_year: "",
        total_seats: "",
        fuel_type: "",
        driver_id: "",
        owner_id: "",
        ac_working: false,
        vehicle_type: "",
        delivery_capacity: {
          max_weight: "",
          max_width: "",
          max_height: "",
          max_length: "",
        },
      });
      setCarRegistrationPhoto(null);
      setPhotoPreview(null);
      fetchCars();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Failed to create car",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5">Cars</Typography>
          {offlineNotifications.length > 0 && (
            <Badge badgeContent={offlineNotifications.length} color="error">
              <Button
                variant="outlined"
                startIcon={<Notifications />}
                onClick={() => {
                  const offlineList = offlineNotifications
                    .map(
                      (car) =>
                        `${car.car_make} ${car.car_model} - ${car.driver_name || "Unknown"} (${car.offline_since || "Unknown time"})`
                    )
                    .join(", ");
                  setSnackbar({
                    open: true,
                    message: `${offlineNotifications.length} car(s) went offline: ${offlineList}`,
                    severity: "warning",
                  });
                }}
              >
                Offline Cars
              </Button>
            </Badge>
          )}
        </Box>
        <Button variant="contained" color="primary" onClick={() => setOpenAddModal(true)} startIcon={<Add />}>
          Add Car
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Make/Model</TableCell>
              <TableCell>License Plate</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : cars.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No cars found
                </TableCell>
              </TableRow>
            ) : (
              cars.map((car) => (
                <TableRow key={car._id}>
                  <TableCell>
                    {car.car_make} {car.car_model}
                  </TableCell>
                  <TableCell>{car.license_plate || "N/A"}</TableCell>
                  <TableCell>{car.driver_name || "Unassigned"}</TableCell>
                  <TableCell>{car.owner_name || "Unassigned"}</TableCell>
                  <TableCell>
                    <Chip
                      label={car.is_offline ? "Offline" : car.status || "Active"}
                      color={car.is_offline ? "error" : car.status === "ACTIVE" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleEditCar(car)} sx={{ mr: 1 }}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => handleAssignDriver(car)} sx={{ mr: 1 }}>
                      Assign Driver
                    </Button>
                    <Button size="small" onClick={() => handleAssignOwner(car)}>
                      Assign Owner
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Car Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Car</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Driver (Optional)</InputLabel>
              <Select
                name="driver_id"
                value={formData.driver_id}
                onChange={handleSelectChange}
                label="Select Driver (Optional)"
              >
                <MenuItem value="">
                  <em>None - Assign Later</em>
                </MenuItem>
                {drivers.map((driver) => {
                  if (driver.isPending) return null; // Don't show pending drivers
                  const displayName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim() || driver.email;
                  return (
                    <MenuItem key={driver._id} value={driver._id}>
                      {displayName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Select Owner (Optional)</InputLabel>
              <Select
                name="owner_id"
                value={formData.owner_id}
                onChange={handleSelectChange}
                label="Select Owner (Optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {owners.map((owner) => (
                  <MenuItem key={owner._id} value={owner._id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Car Make</InputLabel>
              <Select
                name="car_make"
                value={formData.car_make}
                onChange={handleSelectChange}
                label="Car Make"
              >
                {carMakes.map((make) => (
                  <MenuItem key={make} value={make}>
                    {make}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Car Model & Color"
              value={formData.car_model}
              onChange={(e) => setFormData({ ...formData, car_model: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="License Plate"
              value={formData.license_plate}
              onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Year"
              type="number"
              value={formData.car_year}
              onChange={(e) => setFormData({ ...formData, car_year: e.target.value })}
              fullWidth
            />
            <TextField
              label="Total Seats"
              type="number"
              value={formData.total_seats}
              onChange={(e) => setFormData({ ...formData, total_seats: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select
                name="fuel_type"
                value={formData.fuel_type}
                onChange={handleSelectChange}
                label="Fuel Type"
              >
                {fuelTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleSelectChange}
                label="Vehicle Type"
              >
                <MenuItem value="">None</MenuItem>
                {vehicleTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.ac_working}
                    onChange={(e) => setFormData({ ...formData, ac_working: e.target.checked })}
                  />
                }
                label="AC Working"
              />
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Delivery Capacity (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Max Weight (kg)"
                type="number"
                value={formData.delivery_capacity.max_weight}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    delivery_capacity: { ...formData.delivery_capacity, max_weight: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Max Width (cm)"
                type="number"
                value={formData.delivery_capacity.max_width}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    delivery_capacity: { ...formData.delivery_capacity, max_width: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Max Height (cm)"
                type="number"
                value={formData.delivery_capacity.max_height}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    delivery_capacity: { ...formData.delivery_capacity, max_height: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Max Length (cm)"
                type="number"
                value={formData.delivery_capacity.max_length}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    delivery_capacity: { ...formData.delivery_capacity, max_length: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Car Registration Photo
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ mb: 1 }}
              >
                Upload Photo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {photoPreview && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={photoPreview}
                    alt="Car Registration Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setCarRegistrationPhoto(null);
                      setPhotoPreview(null);
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAddModal(false);
            setCarRegistrationPhoto(null);
            setPhotoPreview(null);
          }}>Cancel</Button>
          <Button onClick={handleCreateCar} variant="contained" disabled={uploading}>
            {uploading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Car Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Car</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Driver (Optional)</InputLabel>
              <Select
                name="driver_id"
                value={editFormData.driver_id}
                onChange={(e) => setEditFormData({ ...editFormData, driver_id: e.target.value })}
                label="Select Driver (Optional)"
              >
                <MenuItem value="">
                  <em>None - Unassign Driver</em>
                </MenuItem>
                {drivers.map((driver) => {
                  if (driver.isPending) return null;
                  const displayName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim() || driver.email;
                  return (
                    <MenuItem key={driver._id} value={driver._id}>
                      {displayName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Select Owner (Optional)</InputLabel>
              <Select
                name="owner_id"
                value={editFormData.owner_id}
                onChange={(e) => setEditFormData({ ...editFormData, owner_id: e.target.value })}
                label="Select Owner (Optional)"
              >
                <MenuItem value="">
                  <em>None - Unassign Owner</em>
                </MenuItem>
                {owners.map((owner) => (
                  <MenuItem key={owner._id} value={owner._id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Car Make</InputLabel>
              <Select
                name="car_make"
                value={editFormData.car_make}
                onChange={(e) => setEditFormData({ ...editFormData, car_make: e.target.value })}
                label="Car Make"
              >
                {carMakes.map((make) => (
                  <MenuItem key={make} value={make}>
                    {make}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Car Model & Color"
              value={editFormData.car_model}
              onChange={(e) => setEditFormData({ ...editFormData, car_model: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="License Plate"
              value={editFormData.license_plate}
              onChange={(e) => setEditFormData({ ...editFormData, license_plate: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Year"
              type="number"
              value={editFormData.car_year}
              onChange={(e) => setEditFormData({ ...editFormData, car_year: e.target.value })}
              fullWidth
            />
            <TextField
              label="Total Seats"
              type="number"
              value={editFormData.total_seats}
              onChange={(e) => setEditFormData({ ...editFormData, total_seats: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select
                name="fuel_type"
                value={editFormData.fuel_type}
                onChange={(e) => setEditFormData({ ...editFormData, fuel_type: e.target.value })}
                label="Fuel Type"
              >
                {fuelTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                name="vehicle_type"
                value={editFormData.vehicle_type}
                onChange={(e) => setEditFormData({ ...editFormData, vehicle_type: e.target.value })}
                label="Vehicle Type"
              >
                <MenuItem value="">None</MenuItem>
                {vehicleTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.ac_working}
                    onChange={(e) => setEditFormData({ ...editFormData, ac_working: e.target.checked })}
                  />
                }
                label="AC Working"
              />
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Delivery Capacity (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Max Weight (kg)"
                type="number"
                value={editFormData.delivery_capacity.max_weight}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    delivery_capacity: { ...editFormData.delivery_capacity, max_weight: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Max Width (cm)"
                type="number"
                value={editFormData.delivery_capacity.max_width}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    delivery_capacity: { ...editFormData.delivery_capacity, max_width: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Max Height (cm)"
                type="number"
                value={editFormData.delivery_capacity.max_height}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    delivery_capacity: { ...editFormData.delivery_capacity, max_height: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
              <TextField
                label="Max Length (cm)"
                type="number"
                value={editFormData.delivery_capacity.max_length}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    delivery_capacity: { ...editFormData.delivery_capacity, max_length: e.target.value },
                  })
                }
                fullWidth
                size="small"
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Car Registration Photo
              </Typography>
              {editPhotoPreview && (
                <Box sx={{ mb: 1 }}>
                  <img
                    src={editPhotoPreview}
                    alt="Car Registration Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {editCarRegistrationPhoto ? "New photo selected" : "Current photo"}
                  </Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {editPhotoPreview ? "Change Photo" : "Upload Photo"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleEditFileChange}
                />
              </Button>
              {editCarRegistrationPhoto && (
                <Button
                  size="small"
                  onClick={() => {
                    setEditCarRegistrationPhoto(null);
                    // Restore original preview if it existed
                    const documents: Array<{name: string; url: string}> | string[] = selectedCar?.list_document_url || selectedCar?.list_document_id || [];
                    let originalPhotoUrl: string | null = null;
                    if (Array.isArray(documents) && documents.length > 0) {
                      // Type guard to check if it's an array of objects
                      if (documents.length > 0 && typeof documents[0] === 'object' && documents[0] !== null && 'name' in documents[0]) {
                        const photoDoc = (documents as Array<{name: string; url: string}>).find((doc) => 
                          doc.name === "CarRegistrationPhoto"
                        );
                        if (photoDoc) {
                          originalPhotoUrl = photoDoc.url;
                        } else if (documents[0] && 'url' in documents[0]) {
                          originalPhotoUrl = (documents[0] as {name: string; url: string}).url;
                        }
                      } else {
                        // It's an array of strings
                        const photoDoc = (documents as string[]).find((doc) => 
                          typeof doc === 'string' && doc.includes("CarRegistrationPhoto")
                        );
                        if (photoDoc) {
                          originalPhotoUrl = photoDoc;
                        } else if (documents[0] && typeof documents[0] === 'string') {
                          originalPhotoUrl = documents[0];
                        }
                      }
                    }
                    setEditPhotoPreview(originalPhotoUrl);
                  }}
                  sx={{ mt: 1 }}
                >
                  Remove New Photo
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditModal(false);
            setSelectedCar(null);
            setEditCarRegistrationPhoto(null);
            setEditPhotoPreview(null);
          }}>Cancel</Button>
          <Button onClick={handleUpdateCar} variant="contained" disabled={uploading}>
            {uploading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Driver Modal */}
      <Dialog open={openAssignDriverModal} onClose={() => setOpenAssignDriverModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Driver to Car</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Driver</InputLabel>
              <Select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                label="Select Driver"
              >
                {drivers.map((driver) => (
                  <MenuItem key={driver._id} value={driver._id}>
                    {driver.firstName} {driver.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDriverModal(false)}>Cancel</Button>
          <Button onClick={handleSubmitAssignDriver} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Owner Modal */}
      <Dialog open={openAssignOwnerModal} onClose={() => setOpenAssignOwnerModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Owner to Car</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Owner</InputLabel>
              <Select
                value={selectedOwnerId}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
                label="Select Owner"
              >
                <MenuItem value="">None</MenuItem>
                {owners.map((owner) => (
                  <MenuItem key={owner._id} value={owner._id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignOwnerModal(false)}>Cancel</Button>
          <Button onClick={handleSubmitAssignOwner} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerCars;
